import { cva } from 'class-variance-authority';
import { FontWeights } from '../enums/font-weights';
import { TextSizes } from '../enums/typography-text-sizes';
import { BadgeColors, BadgeSizes, BadgeVariants } from '../enums/variants';
import { textVariants } from './typography-text-variants';

// Class strings stay fully static — Tailwind v4 cannot resolve template interpolation.
const COLORED_CLASS: Record<BadgeColors, string> = {
  [BadgeColors.GRAY]:
    'bg-utility-neutral-50 border-utility-neutral-200 text-utility-neutral-700',
  [BadgeColors.BRAND]:
    'bg-utility-brand-50 border-utility-brand-200 text-utility-brand-700',
  [BadgeColors.ERROR]:
    'bg-utility-red-50 border-utility-red-200 text-utility-red-700',
  [BadgeColors.WARNING]:
    'bg-utility-yellow-50 border-utility-yellow-200 text-utility-yellow-700',
  [BadgeColors.SUCCESS]:
    'bg-utility-green-50 border-utility-green-200 text-utility-green-700',
  [BadgeColors.SLATE]:
    'bg-utility-slate-50 border-utility-slate-200 text-utility-slate-700',
  [BadgeColors.SKY]:
    'bg-utility-sky-50 border-utility-sky-200 text-utility-sky-700',
  [BadgeColors.BLUE]:
    'bg-utility-blue-50 border-utility-blue-200 text-utility-blue-700',
  [BadgeColors.INDIGO]:
    'bg-utility-indigo-50 border-utility-indigo-200 text-utility-indigo-700',
  [BadgeColors.PURPLE]:
    'bg-utility-purple-50 border-utility-purple-200 text-utility-purple-700',
  [BadgeColors.PINK]:
    'bg-utility-pink-50 border-utility-pink-200 text-utility-pink-700',
  [BadgeColors.ORANGE]:
    'bg-utility-orange-50 border-utility-orange-200 text-utility-orange-700',
  [BadgeColors.WHITE]:
    'bg-white border-utility-neutral-200 text-utility-neutral-700',
};

const DOT_TEXT_CLASS: Record<BadgeColors, string> = {
  [BadgeColors.GRAY]: 'text-utility-neutral-700',
  [BadgeColors.BRAND]: 'text-utility-brand-700',
  [BadgeColors.ERROR]: 'text-utility-red-700',
  [BadgeColors.WARNING]: 'text-utility-yellow-700',
  [BadgeColors.SUCCESS]: 'text-utility-green-700',
  [BadgeColors.SLATE]: 'text-utility-slate-700',
  [BadgeColors.SKY]: 'text-utility-sky-700',
  [BadgeColors.BLUE]: 'text-utility-blue-700',
  [BadgeColors.INDIGO]: 'text-utility-indigo-700',
  [BadgeColors.PURPLE]: 'text-utility-purple-700',
  [BadgeColors.PINK]: 'text-utility-pink-700',
  [BadgeColors.ORANGE]: 'text-utility-orange-700',
  [BadgeColors.WHITE]: 'text-utility-neutral-700',
};

// NAKED/MODERN keep neutral chrome; color only tints svg icons. Vendor <img> ignores color.
const ICON_TINT_CLASS: Record<BadgeColors, string> = {
  [BadgeColors.GRAY]: '[&>svg]:text-utility-neutral-700',
  [BadgeColors.BRAND]: '[&>svg]:text-utility-brand-700',
  [BadgeColors.ERROR]: '[&>svg]:text-utility-red-700',
  [BadgeColors.WARNING]: '[&>svg]:text-utility-yellow-700',
  [BadgeColors.SUCCESS]: '[&>svg]:text-utility-green-700',
  [BadgeColors.SLATE]: '[&>svg]:text-utility-slate-700',
  [BadgeColors.SKY]: '[&>svg]:text-utility-sky-700',
  [BadgeColors.BLUE]: '[&>svg]:text-utility-blue-700',
  [BadgeColors.INDIGO]: '[&>svg]:text-utility-indigo-700',
  [BadgeColors.PURPLE]: '[&>svg]:text-utility-purple-700',
  [BadgeColors.PINK]: '[&>svg]:text-utility-pink-700',
  [BadgeColors.ORANGE]: '[&>svg]:text-utility-orange-700',
  [BadgeColors.WHITE]: '[&>svg]:text-utility-neutral-700',
};

const COLORS = Object.values(BadgeColors);

const coloredCompounds = COLORS.map(color => ({
  variant: BadgeVariants.COLORED,
  color,
  class: COLORED_CLASS[color],
}));

const modernIconCompounds = COLORS.map(color => ({
  variant: BadgeVariants.MODERN,
  color,
  class: ICON_TINT_CLASS[color],
}));

const nakedIconCompounds = COLORS.map(color => ({
  variant: BadgeVariants.NAKED,
  color,
  class: ICON_TINT_CLASS[color],
}));

const TEXT_SM = textVariants({
  size: TextSizes.XS,
  weight: FontWeights.MEDIUM,
});
const TEXT_MD = textVariants({
  size: TextSizes.SM,
  weight: FontWeights.MEDIUM,
});

export const badgeVariants = cva(
  'inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 tracking-normal cursor-default [&_svg]:shrink-0 [&>img]:shrink-0',
  {
    variants: {
      size: {
        [BadgeSizes.SM]: `h-[22px] rounded-[6px] px-1.5 py-0.5 ${TEXT_SM}`,
        [BadgeSizes.MD]: `h-6 rounded-[6px] px-2 py-0.5 ${TEXT_MD}`,
        [BadgeSizes.LG]: `h-7 rounded-[8px] px-2.5 py-1 ${TEXT_MD}`,
      },
      variant: {
        [BadgeVariants.NAKED]: 'border-0 text-utility-neutral-700',
        [BadgeVariants.MODERN]:
          'border bg-white border-utility-neutral-200 text-utility-neutral-700',
        [BadgeVariants.COLORED]: 'border',
      },
      color: COLORS.reduce<Record<BadgeColors, string>>(
        (acc, color) => {
          acc[color] = '';
          return acc;
        },
        {} as Record<BadgeColors, string>
      ),
      hasDot: {
        true: '',
        false: '',
      },
      hasRemove: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      ...coloredCompounds,
      ...modernIconCompounds,
      ...nakedIconCompounds,
      // Gap when dot present — varies by size per Figma spec
      { size: BadgeSizes.SM, hasDot: true, class: 'gap-1' },
      { size: BadgeSizes.MD, hasDot: true, class: 'gap-1.5' },
      { size: BadgeSizes.LG, hasDot: true, class: 'gap-1.5' },
      // Right padding tweak when remove button present
      { size: BadgeSizes.SM, hasRemove: true, class: 'gap-1 pr-1' },
      { size: BadgeSizes.MD, hasRemove: true, class: 'gap-1 pr-1.5' },
      { size: BadgeSizes.LG, hasRemove: true, class: 'gap-1 pr-2' },
      // Figma 1046-3819: skip when dot/remove already drive layout.
      {
        size: BadgeSizes.SM,
        hasDot: false,
        hasRemove: false,
        class: [
          'gap-0.5',
          '[&>[data-slot=avatar]:first-child:not(:only-child)]:mr-0.5',
          '[&:has(>:is(svg,img):first-child:not(:only-child))]:pr-2',
          '[&:has(>[data-slot=avatar]:first-child:not(:only-child))]:pr-2',
          '[&:has(>:is(svg,img):last-child:not(:first-child))]:pl-2',
          '[&:has(>:is(svg,img):only-child)]:size-[22px]',
          '[&:has(>:is(svg,img):only-child)]:p-1',
        ].join(' '),
      },
      {
        size: BadgeSizes.MD,
        hasDot: false,
        hasRemove: false,
        class: [
          'gap-1',
          '[&>[data-slot=avatar]:first-child:not(:only-child)]:mr-0.5',
          '[&:has(>:is(svg,img):first-child:not(:only-child))]:pl-1.5',
          '[&:has(>[data-slot=avatar]:first-child:not(:only-child))]:pl-1.5',
          '[&:has(>:is(svg,img):last-child:not(:first-child))]:pr-1.5',
          '[&:has(>:is(svg,img):only-child)]:size-6',
          '[&:has(>:is(svg,img):only-child)]:p-[5px]',
        ].join(' '),
      },
      {
        size: BadgeSizes.LG,
        hasDot: false,
        hasRemove: false,
        class: [
          'gap-1',
          '[&>[data-slot=avatar]:first-child:not(:only-child)]:mr-0.5',
          '[&:has(>:is(svg,img):first-child:not(:only-child))]:pl-2',
          '[&:has(>[data-slot=avatar]:first-child:not(:only-child))]:pl-2',
          '[&:has(>:is(svg,img):last-child:not(:first-child))]:pr-2',
          '[&:has(>:is(svg,img):only-child)]:size-7',
          '[&:has(>:is(svg,img):only-child)]:p-1.5',
        ].join(' '),
      },
    ],
    defaultVariants: {
      variant: BadgeVariants.COLORED,
      color: BadgeColors.GRAY,
      size: BadgeSizes.MD,
      hasDot: false,
      hasRemove: false,
    },
  }
);

export const badgeDotVariants = cva(
  'inline-flex items-center justify-center size-2 shrink-0',
  {
    variants: {
      color: DOT_TEXT_CLASS,
    },
    defaultVariants: {
      color: BadgeColors.GRAY,
    },
  }
);
